import React, { useEffect, useState, useMemo } from 'react';
import Breadcrumb from '../../components/BreadCrumb/BreadCrumb';
import CustomCard from '../../components/Common/CustomCard/CustomCard';
import ProjectTimelineTable from '../../components/Common/CustomTable/ProjectTable/ProjectTimelineTable';
import { useDispatch, useSelector } from 'react-redux';
import {
        fetchDownloadExcel,
        fetchRegionAllocationSummary,
        fetchRegionMilestoneSummary,
        fetchRegionProjectDates,
        fetchRegionProjectSummary,
        getCashInflowData,
        getCashInflowDataVersion,
        getCashOutflowData,
        getCashOutflowDataVersioned,
        getApportionedCost,
        getApportionedCostVersioned,
        getBillingCost,
        getBillingCostVersioned,
        getCustomCardDetail,
        getCustomCardDetailVersion,
        getOperationalIncome,
        getOperationalIncomeVersioned,
        getProfitLoss,
        getProfitLossVersion,
        getProjectCost,
        getProjectCostVersion,
        getProjectRevenue,
        getProjectRevenueVersion,
        fetchRegionProjectSummaryVersioned,
} from '../../redux/budgetSlice/budgetSlice';
import { CalendarOutlined } from '@ant-design/icons';
import MileStoneTable from '../../components/Common/CustomTable/ProjectTable/MileStoneTable';
import CustomTable from '../../components/Common/CustomTable/CustomTable';
import ProjectSummaryTable from '../../components/Common/CustomTable/ProjectTable/ProjectSummaryTable';
import ProjectRevenueTable from '../../components/Common/CustomTable/ProjectTable/ProjectRevenueTable';
import { format, parse } from 'date-fns';
import ProjectCostTable from '../../components/Common/CustomTable/ProjectTable/ProjectCostTable';
import ProjectApportionedTable from '../../components/Common/CustomTable/ProjectTable/ProjectApportionedTable';
import OtherOperationalIncome from '../../components/Common/CustomTable/ProjectTable/OtherOperationalIncome';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ProjectLossTable from '../../components/Common/CustomTable/ProjectTable/ProfitLossTable';
import ProjectBillingTable from '../../components/Common/CustomTable/ProjectTable/ProjectBillingTable';
import ProjectCashFlowTable from '../../components/Common/CustomTable/ProjectTable/ProjectCashFlowTable';
import { customCardSection } from '../BudgetSubmission/constants';
import Loader from '../../components/Common/LoadingSpinner/LoadingSpinner';
import { DatePicker, Select } from 'antd';
import DownloadCsvIcon from '../../assets/icons/download-csv-icon.svg';
import CustomSelectWithFilter from '../../components/Common/CustomSelect/CustomSelectWithFilter';
import dayjs from "dayjs";

const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

const UnitRegionAdminPreview = () => {
        const dispatch = useDispatch();
        const { projectDates, milestoneSummary, allocationSummary, projectRevenueData, projectRevenueVersion, projectCostCompleteData, projectCostCompleteVersion, apportionedCostCompleteData, apportionedCompleteVersion, cardData, cardDataVersion, billingCostCompleteData, billingCostCompleteDataVersion } = useSelector((state) => state.budget);
        const [version, setVersion] = useState("C");
        const formattedUptoDate = projectDates?.UPTO_DATE ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy") : '';
        const formattedStartDate = projectDates?.START_DATE ? format(parse(projectDates.START_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy") : '';
        const isCurrentVersion = version === "C";
        const { systemDate } = useSelector((state) => state.otherProjects);

        const getCurrentFYRange = () => {
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth() + 1;

                const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;

                return {
                        fromDate: dayjs(`${fyStartYear}-04-01`),
                        toDate: dayjs(`${fyStartYear + 1}-03-01`),
                        fyStartYear,
                };
        };

        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        const { fromDate, toDate, fyStartYear } = getCurrentFYRange();


        const [loading, setLoading] = useState(false);
        const { RangePicker } = DatePicker;
        const [selectedDateRange, setSelectedDateRange] = useState([
                fromDate,
                toDate,
        ]);
        const [monthRange, setMonthRange] = useState({
                from_year: String(fyStartYear),
                to_year: String(fyStartYear + 1),
                from_month: "04",
                to_month: "03",
        });
        const { unitRegionProject } = useParams();
        const [searchParams] = useSearchParams();
        const unitType = searchParams?.get('unitType')?.toUpperCase(); // automatically decoded
        const mnfUnit = searchParams?.get('mnfUnit')?.toUpperCase(); // automatically decoded
        const IO_NO = searchParams?.get('ioNo')?.toUpperCase();
        const REV_NO = searchParams?.get('revNo')?.toUpperCase();

        function fetchUnitRegionNameFromRoute() {
                const formattedUnitRegionName = decodeURIComponent(unitRegionProject).replace(/\s+/g, ' ');
                return formattedUnitRegionName;
        }

        const handleMonthRangeChange = (dates) => {
                setSelectedDateRange(dates);

                if (dates && dates.length === 2) {
                        const fromDate = dates[0].toDate();
                        const toDate = dates[1].toDate();

                        setMonthRange({
                                from_year: format(fromDate, 'yyyy'),
                                to_year: format(toDate, 'yyyy'),
                                from_month: format(fromDate, 'MM'),
                                to_month: format(toDate, 'MM'),
                        });
                } else {
                        setMonthRange({
                                from_year: null,
                                to_year: null,
                                from_month: null,
                                to_month: null,
                        });
                }
        };

        //API Calling for Project Dates
        const projectTimelineColumns = [
                {
                        title: 'Zero Date',
                        dataIndex: 'ZERO_DATE',
                        key: 'ZERO_DATE',
                        width: '25%',
                        align: 'center',
                        render: (date) => <div>{date ?? <CalendarOutlined />}</div>,
                },
                {
                        title: 'Scheduled Completion Date',
                        dataIndex: 'SCHEDULE_DATE',
                        key: 'SCHEDULE_DATE',
                        width: '25%',
                        align: 'center',

                        render: (date) => <div>{date ?? <CalendarOutlined />}</div>,
                },
                {
                        title: 'Expected Completion Date',
                        dataIndex: 'EXPECTED_DATE',
                        key: 'EXPECTED_DATE',
                        width: '25%',
                        align: 'center',

                        render: (date) => <div>{date ?? <CalendarOutlined />}</div>,
                },
                {
                        title: 'Unit Plan Date',
                        dataIndex: 'UNIT_PLAN_DATE',
                        key: 'UNIT_PLAN_DATE',
                        width: '25%',
                        align: 'center',

                        render: (date) => <div>{date ?? <CalendarOutlined />}</div>,
                },
        ];

        // For milestone summary
        const milestoneData = milestoneSummary?.projectData?.map((unit, index) => {
                const rowData = {
                        key: index + 1,
                        unit: `Unit #${unit.UNIT_NO}`,
                };

                unit.dates?.forEach((date) => {
                        const formattedKey = date.MS_DESC.toLowerCase().replace(/\s+/g, '');
                        rowData[`${formattedKey}Schedule`] = date.SCHEDULE_DATE;
                        rowData[`${formattedKey}Actual`] = date.ACTUAL_DATE || date.EXPECTED_DATE;
                });

                return rowData;
        });

        const milestoneColumnsData = milestoneSummary?.milestones?.map((milestone) => ({
                title: milestone?.MS_DESC, // Using MS_DESC as title
                children: [
                        {
                                label: 'Schedule Date',
                                dataIndex: `${milestone?.MS_DESC.toLowerCase().replace(/\s+/g, '')}Schedule`,
                        },
                        {
                                label: 'Actual / Expected Date',
                                dataIndex: `${milestone?.MS_DESC.toLowerCase().replace(/\s+/g, '')}Actual`,
                        },
                ],
        }));
        //For Milestone SUmmary
        const allocationSummaryData = [
                allocationSummary?.details?.reduce(
                        (acc, item, index) => {
                                acc[`allocatedValue${index}`] = item.ALLOCATED_VALUE; // Assign value dynamically
                                return acc;
                        },
                        { key: '1', total: allocationSummary.total },
                ),
        ];

        const allocationSummaryColumn = allocationSummary?.details?.map((item, index) => ({
                title: item.DESC,
                dataIndex: `allocatedValue${index}`,
                key: `allocatedValue${index}`,
                width: 200,
                align: 'center',
        }));

        allocationSummaryColumn?.push({
                title: 'Total',
                dataIndex: 'total',
                width: 200,
                key: 'total',
                align: 'center',
        });

        const projectRevenuecolumns1 = [
                {
                        title: 'Original Order Allocation',
                        dataIndex: 'ALLOCATION',
                        key: 'ALLOCATION',
                        align: 'center',
                        width: '25%',
                        render: (text) => <div className="text-right">{text}</div>,
                },
                {
                        title: 'PVC',
                        dataIndex: 'PVC',
                        key: 'PVC',
                        align: 'center',
                        width: '25%',
                        render: (text) => <div className="text-right">{text}</div>,
                },
                {
                        title: 'ERV',
                        dataIndex: 'ERV',
                        key: 'ERV',
                        align: 'center',
                        width: '25%',
                        render: (text) => <div className="text-right">{text}</div>,
                },
                {
                        title: 'Other Adjustments',
                        dataIndex: 'OTHER_ADJUS',
                        key: 'OTHER_ADJUS',
                        align: 'center',
                        width: '25%',
                        render: (text) => <div className="text-right">{text}</div>,
                },
        ];

        const projectRevenuecolumns2 = [
                {
                        title: 'Revised Order Book (TCR)',
                        dataIndex: 'TCR',
                        key: 'TCR',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        // title: `Revenue Recognised Upto ${formattedStartDate}`,
                        title: `Revenue Recognised Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'REVENUE_RECOGNISED_UPTO',
                        key: 'REVENUE_RECOGNISED_UPTO',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `Balance O/s as  Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'BALNACE_REVENUE',
                        key: 'BALNACE_REVENUE',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: 'Current Factor (Unit/region level)',
                        dataIndex: 'CURRENT_FACTOR',
                        key: 'CURRENT_FACTOR',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const projectRevenueData1 = [
                {
                        key: '1',
                        ALLOCATION: version === "C" ? projectRevenueData?.ALLOCATION : projectRevenueVersion?.ALLOCATION,
                        PVC: version === "C" ? projectRevenueData?.PVC : projectRevenueVersion?.PVC,
                        ERV: version === "C" ? projectRevenueData?.ERV : projectRevenueVersion?.ERV,
                        OTHER_ADJUS: version === "C" ? projectRevenueData?.OTHER_ADJUS : projectRevenueVersion?.OTHER_ADJUS,
                },
        ];

        const projectRevenueData2 = [
                {
                        key: '1',
                        TCR: version === "C" ? projectRevenueData?.TCR : projectRevenueVersion?.TCR,
                        REVENUE_RECOGNISED_UPTO: version === "C" ? projectRevenueData?.REVENUE_RECOGNISED_UPTO : projectRevenueVersion?.REVENUE_RECOGNISED_UPTO,
                        BALNACE_REVENUE: version === "C" ? projectRevenueData?.BALNACE_REVENUE : projectRevenueVersion?.BALNACE_REVENUE,
                        CURRENT_FACTOR: typeof (version === "C" ? projectRevenueData?.CURRENT_FACTOR : projectRevenueVersion?.CURRENT_FACTOR) === 'number' ? (version === "C" ? projectRevenueData.CURRENT_FACTOR : projectRevenueVersion.CURRENT_FACTOR).toFixed(4) : '0.00',
                },
        ];

        const revenueHeading = {
                firstHeading: 'head',
                secondHeading: "Upto March'25",
                thirdHeading: 'Total 24-26',
        };

        const projectCostData = [
                {
                        key: '1',
                        TECC: version === "C" ? projectCostCompleteData?.TECC : projectCostCompleteVersion?.TECC,
                        PROJ_COST_TECC: version === "C" ? projectCostCompleteData?.PROJ_COST_TECC : projectCostCompleteVersion?.PROJ_COST_TECC,
                        CCI: version === "C" ? projectCostCompleteData?.CCI : projectCostCompleteVersion?.CCI,
                        BALANCE_COST: version === "C" ? projectCostCompleteData?.BALANCE_COST : projectCostCompleteVersion?.BALANCE_COST,
                },
        ];
        const projectCostColumns = [
                {
                        title: 'Initial Estimate (TECC)',
                        dataIndex: 'PROJ_COST_TECC',
                        key: 'PROJ_COST_TECC',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: 'Revised TECC',
                        dataIndex: 'TECC',
                        key: 'TECC',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `CCI Upto  Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'CCI',
                        key: 'CCI',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `Balance Cost as on Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'BALANCE_COST',
                        key: 'BALANCE_COST',
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const projectApportionedColumns = [
                {
                        title: 'Initial Estimate',
                        dataIndex: 'APORTION_COST',
                        key: 'APORTION_COST',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `CCI Upto  Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'CCI_UPTO',
                        key: 'CCI_UPTO',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `Balance Cost Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'BALANCE_COST',
                        key: 'BALANCE_COST',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const projectApportionedData = [
                {
                        key: '1',
                        APORTION_COST: version === "C" ? apportionedCostCompleteData?.APORTION_COST : apportionedCompleteVersion?.APORTION_COST,
                        CCI_UPTO: version === "C" ? apportionedCostCompleteData?.CCI_UPTO : apportionedCompleteVersion?.CCI_UPTO,
                        BALANCE_COST: version === "C" ? apportionedCostCompleteData?.BALANCE_COST : apportionedCompleteVersion?.BALANCE_COST,
                },
        ];

        const projectBillingData = [
                {
                        key: '1',
                        revisedOrderAllocation: version === "C" ? billingCostCompleteData?.TCR : billingCostCompleteDataVersion?.TCR,
                        billedUpto: version === "C" ? billingCostCompleteData?.BILLED_UPTO : billingCostCompleteDataVersion?.BILLED_UPTO,
                        balanceToBeBilled: version === "C" ? billingCostCompleteData?.BILLED_BALANCE : billingCostCompleteDataVersion?.BILLED_BALANCE,
                },
        ];

        const projectBillingColumns = [
                {
                        title: 'Revised Order Allocation (TCR)',
                        dataIndex: 'revisedOrderAllocation',
                        key: 'revisedOrderAllocation',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: `Billed Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'billedUpto',
                        key: 'billedUpto',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
                {
                        title: 'Balance to be billed',
                        dataIndex: 'balanceToBeBilled',
                        key: 'balanceToBeBilled',
                        align: 'center',

                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];
        const handleDownload = (version) => {
                const fileName = ['project_cost', 'other_apportioned_cost', "other_operational_income", "billing", "cash_inflow", "cash_outflow"]
                fileName.forEach((fileName) => {
                        dispatch(fetchDownloadExcel({ fileType: fileName, ioNo: IO_NO, revNo: REV_NO, version, unitType, mnfUnit }));
                });
        };
        const fetchAllData = async () => {
                setLoading(true);

                try {
                        const payload = {
                                unitType,
                                mnfUnit,
                                ioNo: IO_NO,
                                revNo: REV_NO,
                        };

                        const apiCalls = isCurrentVersion
                                ? [
                                        dispatch(getCustomCardDetail(payload)).unwrap(),
                                        dispatch(getProjectRevenue(payload)).unwrap(),
                                        dispatch(getProjectCost(payload)).unwrap(),
                                        dispatch(getProfitLoss(payload)).unwrap(),
                                        dispatch(getCashInflowData(payload)).unwrap(),
                                        dispatch(getCashOutflowData(payload)).unwrap(),
                                        dispatch(fetchRegionAllocationSummary(payload)).unwrap(),
                                        dispatch(fetchRegionMilestoneSummary(payload)).unwrap(),
                                        dispatch(fetchRegionProjectDates(payload)).unwrap(),
                                        dispatch(fetchRegionProjectSummary(payload)).unwrap(),
                                        dispatch(getApportionedCost(payload)).unwrap(),
                                        dispatch(getBillingCost(payload)).unwrap(),
                                        dispatch(getOperationalIncome(payload)).unwrap(),
                                ]
                                : [
                                        dispatch(getCustomCardDetailVersion({ ...payload, version })).unwrap(),
                                        dispatch(getProjectRevenueVersion({ ...payload, version })).unwrap(),
                                        dispatch(getProjectCostVersion({ ...payload, version })).unwrap(),
                                        dispatch(getProfitLossVersion({ ...payload, version })).unwrap(),
                                        dispatch(getCashInflowDataVersion({ ...payload, version })).unwrap(),
                                        dispatch(getCashOutflowDataVersioned({ ...payload, version })).unwrap(),
                                        dispatch(fetchRegionAllocationSummary(payload)).unwrap(),
                                        dispatch(fetchRegionMilestoneSummary(payload)).unwrap(),
                                        dispatch(fetchRegionProjectDates(payload)).unwrap(),
                                        dispatch(fetchRegionProjectSummaryVersioned({ ...payload, version })).unwrap(),
                                        dispatch(getApportionedCostVersioned({ ...payload, version })).unwrap(),
                                        dispatch(getBillingCostVersioned({ ...payload, version })).unwrap(),
                                        dispatch(getOperationalIncomeVersioned({ ...payload, version })).unwrap(),
                                ];

                        await Promise.all(apiCalls);
                } catch (err) {
                        console.error("Failed to fetch data", err);
                } finally {
                        setLoading(false);
                }
        };

        useEffect(() => {


                if (IO_NO && REV_NO && mnfUnit && unitType) {
                        fetchAllData();
                }
        }, [IO_NO, REV_NO, mnfUnit, unitType, version]);

        useEffect(() => {
                setLoading(true);
                if (version === "C") {
                        dispatch(
                                getCustomCardDetail({
                                        unitType,
                                        mnfUnit,
                                        ioNo: IO_NO,
                                        revNo: REV_NO,
                                        monthRange: monthRange,
                                }),
                        ).then(() => {
                                setLoading(false);
                        });
                } else {
                        dispatch(
                                getCustomCardDetailVersion({
                                        unitType,
                                        mnfUnit,
                                        ioNo: IO_NO,
                                        revNo: REV_NO,
                                        monthRange: monthRange,
                                        version,
                                }),
                        ).then(() => {
                                setLoading(false);
                        });
                }
        }, [monthRange]);

        return (
                <div className="p-5 flex flex-col gap-5">
                        <div className="flex flex-col gap-6">
                                <Breadcrumb queryParams={`unitType=${unitType}&mnfUnit=${mnfUnit}`} />
                                {/*heading and buttons*/}
                                {loading ? (
                                        <Loader />
                                ) : (
                                        <div className="flex flex-col gap-6">
                                                <div className="flex justify-between items-center">
                                                        <div>
                                                                <span className="font-inter font-semibold text-[24px] leading-[32px] tracking-[0%] text-[#101828]">{fetchUnitRegionNameFromRoute()}</span>
                                                                {/* <span className="font-inter font-normal text-[16px] leading-[24px] tracking-[0%] text-[#475467]">
                                                        {'[IKDSKDSDLSKDLDDL]'}
                                                </span> */}
                                                                <div className="text-gray-500 text-sm flex justify-start py-2">* All figures are in Rupees (in Crores).</div>
                                                        </div>

                                                        <div className='flex gap-3'>
                                                                <Select
                                                                        options={[
                                                                                { label: "Version 1", value: "V1" },
                                                                                { label: "Version 2", value: "V2" },

                                                                        ]}
                                                                        //     value={version}
                                                                        onChange={(value) => handleDownload(value)}
                                                                        placeholder="Download Version Report"
                                                                        className="w-[14rem]"
                                                                />
                                                                <Select
                                                                        options={[
                                                                                { label: "Current Version ", value: "C" },
                                                                                { label: "Version 1", value: "V1" },
                                                                                { label: "Version 2", value: "V2" },

                                                                        ]}
                                                                        value={version}
                                                                        onChange={setVersion}
                                                                        placeholder="Current Version"
                                                                        className="w-[14rem]"
                                                                />
                                                                {/* <div className='underline text-[#0000FF] text-xs font-medium'  onClick={() => handleDownload("V1")} >
                                                                  
                                                                  
                                                                  <div className='flex gap-1 cursor-pointer '>
                                                                        <span>Version 1</span> <img src={DownloadCsvIcon} alt="" className='size-4' />
                                                                  </div>
                                                                  
                                                                  </div>
                                                                 
                                                                                                <div className='underline text-[#0000FF] text-xs font-medium' onClick={() => handleDownload("V2")} >
                                                                                                <div className='flex gap-1 cursor-pointer'>
                                                                        <span>Version 2</span> <img src={DownloadCsvIcon} alt=""  className='size-4'/>
                                                                  </div>
                                                                                                </div>
                                                                  */}
                                                        </div>
                                                        {/* <div className="flex gap-3">
                                                <Button text={'Export report'} />
                                                <CustomSelect
                                                        placeholder="All Time"
                                                        width="9rem"
                                                        options={[
                                                                {
                                                                        value: 'not_initiated',
                                                                        label: 'Not Initiated',
                                                                },
                                                                {
                                                                        value: 'incomplete',
                                                                        label: 'Incomplete',
                                                                },
                                                                {
                                                                        value: 'pending',
                                                                        label: 'Pending for Final Submission',
                                                                },
                                                                {
                                                                        value: 'submitted',
                                                                        label: 'Submitted',
                                                                },
                                                        ]}
                                                />
                                        </div> */}
                                                </div>
                                                {/*Custom Cards*/}
                                                <div className="flex justify-end">
                                                        {' '}
                                                        <RangePicker
                                                                picker="month"
                                                                format="MMMM YYYY"
                                                                onChange={handleMonthRangeChange}
                                                                value={selectedDateRange}
                                                                placeholder={['From Month', 'To Month']}
                                                                allowClear
                                                        />
                                                </div>


                                                <div className="grid grid-cols-4 gap-4">
                                                        {customCardSection(version === "C" ? cardData : cardDataVersion).map((obj, index) => (
                                                                <CustomCard
                                                                        key={index}
                                                                        title={obj?.title}
                                                                        value={obj?.value}
                                                                        isPositive={obj?.isPositive}
                                                                        imageIcon={obj?.imageIcon}
                                                                        colorCode={obj?.colorCode}
                                                                        className="!w-[100%]"
                                                                        dmCost={obj?.dmCost}
                                                                />
                                                        ))}
                                                </div>

                                                {/*Timeline Table*/}
                                                <ProjectTimelineTable
                                                        data={[projectDates]}
                                                        columns={projectTimelineColumns}
                                                />
                                                {/*MileStone Table */}
                                                {milestoneSummary?.milestones?.length > 0 && (
                                                        <>
                                                                <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]"> Milestone Summary</div>
                                                                <MileStoneTable
                                                                        columnsData={milestoneColumnsData}
                                                                        tableData={milestoneData}
                                                                />
                                                        </>
                                                )}

                                                {/*Allocation Summary Table*/}
                                                <>
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]"> Allocation Summary</div>
                                                        <CustomTable
                                                                dataSource={allocationSummaryData}
                                                                columns={allocationSummaryColumn}
                                                        />
                                                </>
                                                {/*Project Summary Table*/}
                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Project Summary</div>
                                                        <div>
                                                                <ProjectSummaryTable version={version} />
                                                        </div>
                                                </div>

                                                {/*Project Revenue Table*/}

                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Project Revenue (A)</div>
                                                        <div className="flex flex-col gap-5">
                                                                <div>
                                                                        <ProjectTimelineTable
                                                                                data={projectRevenueData1}
                                                                                columns={projectRevenuecolumns1}
                                                                        />
                                                                </div>
                                                                <div>
                                                                        <ProjectTimelineTable
                                                                                data={projectRevenueData2}
                                                                                columns={projectRevenuecolumns2}
                                                                        />
                                                                </div>
                                                                <div>
                                                                        <ProjectRevenueTable
                                                                                headings={revenueHeading}
                                                                                revenueData={version === "C" ? projectRevenueData : projectRevenueVersion}
                                                                        />
                                                                </div>
                                                        </div>
                                                </div>

                                                {/*Project Cost Table*/}
                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Project Cost (B)</div>
                                                        <div>
                                                                <ProjectTimelineTable
                                                                        data={projectCostData}
                                                                        columns={projectCostColumns}
                                                                />
                                                        </div>
                                                        <div>
                                                                <ProjectCostTable addPackageButton={false} version={version} />
                                                        </div>
                                                </div>

                                                {/*Other Apportioned Cost Table*/}
                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Other Apportioned Cost (C)</div>
                                                        <div>
                                                                <ProjectTimelineTable
                                                                        data={projectApportionedData}
                                                                        columns={projectApportionedColumns}
                                                                />
                                                        </div>
                                                        <div>
                                                                <ProjectApportionedTable version={version} />
                                                        </div>
                                                </div>


                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Other Operational Income (D)</div>
                                                        <div>
                                                                <OtherOperationalIncome version={version} />
                                                        </div>
                                                </div>


                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Profit/Loss (E)</div>

                                                        <div>
                                                                <ProjectLossTable version={version} />
                                                        </div>
                                                </div>

                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Billing (F)</div>
                                                        <div>
                                                                <ProjectTimelineTable
                                                                        data={projectBillingData}
                                                                        columns={projectBillingColumns}
                                                                />
                                                        </div>
                                                        <div>
                                                                <ProjectBillingTable addPackageButton={false} version={version} />
                                                        </div>
                                                </div>

                                                <div className="flex flex-col gap-3">
                                                        <div className="font-medium text-[18px] leading-[28px] tracking-[0%] text-[#475467]">Cash Flow (G)</div>

                                                        <div>
                                                                <ProjectCashFlowTable addPackageButton={false} version={version} />
                                                        </div>
                                                </div>
                                        </div>
                                )}
                        </div>
                </div>
        );
};

export default UnitRegionAdminPreview;
